import { useEffect, useState } from 'react';
import { useBulkGoals, useGoals, useSaveGoal } from '@/hooks/queries';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import {
  EmptyState, ErrorState, Field, Modal, MoneyInput, ProgressBar, StatLine, TableSkeleton,
} from '@/components/ui';
import { IconCheck, IconTarget } from '@/components/Icons';
import { formatBRL, formatBRLShort, formatNumber, formatPercent } from '@/lib/format';
import { CURRENT_YEAR, YEAR_OPTIONS } from '@/lib/years';

export function GoalsPage() {
  const { isAdmin } = useAuth();
  const toast = useToast();

  const [year, setYear] = useState(CURRENT_YEAR);
  const [bulkOpen, setBulkOpen] = useState(false);

  const { data, isLoading, error } = useGoals({ year });
  const saveGoal = useSaveGoal();
  const bulkGoals = useBulkGoals();

  if (error) return <ErrorState message={(error as Error).message} />;

  return (
    <>
      <h1 className="page-title">Metas mensais</h1>
      <p className="page-lede">
        Meta de vendas da operação inteira, mês a mês, contra o valor vendido nos comprovantes.
        {isAdmin ? '' : ' Só administradores alteram os valores.'}
      </p>

      <div className="toolbar">
        <select className="inp" value={year} onChange={(event) => setYear(Number(event.target.value))} aria-label="Ano">
          {YEAR_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
        </select>

        <div className="spacer" />
        {isAdmin ? (
          <button type="button" className="btn" onClick={() => setBulkOpen(true)}>
            <IconTarget size={14} /> Definir meta para o ano inteiro
          </button>
        ) : null}
      </div>

      {isLoading && !data ? (
        <TableSkeleton rows={6} columns={6} />
      ) : !data ? (
        <EmptyState message="Sem dados para o período." />
      ) : (
        <>
          <div className="metric-row" style={{ marginBottom: 30 }}>
            <StatLine label="Meta anual" value={formatBRLShort(data.totals.target)} />
            <StatLine label="Vendido no ano" value={formatBRLShort(data.totals.soldTotal)} color="var(--emphasis)" />
            <StatLine
              label="% da meta atingida"
              value={formatPercent(data.totals.progress)}
              color={data.totals.progress >= 1 ? 'var(--success)' : undefined}
            />
            <StatLine label="Comprovantes no ano" value={formatNumber(data.totals.receiptCount)} />
          </div>

          <div className="table-wrap">
            <table className="tbl tbl-cards">
              <thead>
                <tr>
                  <th>Mês</th>
                  <th className="right">Vendido</th>
                  <th className="right">Comprovantes</th>
                  <th style={{ width: 170 }}>Meta do mês (R$)</th>
                  <th style={{ width: 170 }}>Progresso</th>
                  <th className="right">% atingida</th>
                </tr>
              </thead>
              <tbody>
                {data.months.map((row) => {
                  const tone =
                    row.progress >= 1 ? 'var(--success)' : row.progress >= 0.7 ? 'var(--emphasis)' : 'var(--destructive)';
                  return (
                    <tr key={row.month} className="row-hover">
                      <td className="cell-main" style={{ fontWeight: 500 }}>{row.monthLabel}</td>
                      <td className="num right emphasis cell-third" data-label="Vendido">{formatBRLShort(row.soldTotal)}</td>
                      <td className="num right cell-third" data-label="Comprovantes">{row.receiptCount}</td>
                      <td className="cell-full" data-label="Meta do mês (R$)">
                        {isAdmin ? (
                          <GoalInput
                            value={row.target}
                            onSave={async (target) => {
                              try {
                                await saveGoal.mutateAsync({ year, month: row.month, target });
                                toast.success(`Meta de ${row.monthLabel} salva.`);
                              } catch (caught) {
                                toast.fromError(caught);
                              }
                            }}
                          />
                        ) : (
                          <span className="num">{formatBRL(row.target)}</span>
                        )}
                      </td>
                      <td className="cell-third" data-label="Progresso"><ProgressBar value={row.soldTotal} max={row.target} height={10} /></td>
                      <td className="num right cell-key" style={{ color: row.target ? tone : 'var(--muted-foreground)' }}>
                        {row.target ? formatPercent(row.progress) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {bulkOpen ? (
        <BulkGoalModal
          busy={bulkGoals.isPending}
          onClose={() => setBulkOpen(false)}
          onSubmit={async (target) => {
            try {
              await bulkGoals.mutateAsync({
                year,
                goals: Array.from({ length: 12 }, (_, index) => ({ month: index + 1, target })),
              });
              toast.success(`Meta de ${formatBRL(target)} aplicada aos 12 meses.`);
              setBulkOpen(false);
            } catch (caught) {
              toast.fromError(caught);
            }
          }}
        />
      ) : null}
    </>
  );
}

/** Campo de meta com confirmação explícita — evita salvar a cada tecla. */
function GoalInput({ value, onSave }: { value: number; onSave: (target: number) => Promise<void> }) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);

  const dirty = draft !== value;
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      <MoneyInput value={draft} onChange={setDraft} onEnter={() => dirty && void onSave(draft)} />
      {dirty ? (
        <button type="button" className="btn btn-primary btn-sm" onClick={() => void onSave(draft)} title="Salvar meta">
          <IconCheck />
        </button>
      ) : null}
    </div>
  );
}

function BulkGoalModal({
  busy, onClose, onSubmit,
}: { busy: boolean; onClose: () => void; onSubmit: (target: number) => Promise<void> }) {
  const [target, setTarget] = useState(10_000_000);

  return (
    <Modal
      title="Meta para o ano inteiro"
      width={420}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>Cancelar</button>
          <button type="button" className="btn btn-primary" onClick={() => void onSubmit(target)} disabled={busy}>
            {busy ? <span className="spinner" /> : <IconCheck />} Aplicar aos 12 meses
          </button>
        </>
      }
    >
      <Field label="Meta mensal (R$)" hint="O mesmo valor será gravado em todos os meses do ano selecionado.">
        <MoneyInput value={target} onChange={setTarget} autoFocus />
      </Field>
      <div className="alert alert-info">
        Isso substitui as metas já definidas para este ano.
      </div>
    </Modal>
  );
}
